/**
 * The catalogues, and the type that makes a wrong key a build error.
 *
 * `TKey` is derived from `en.json` — English is the source. `it.json` is asserted, at the type
 * level, to carry exactly the same keys with exactly the same shapes. A missing translation,
 * an extra one, or a key that is plural in one file and a plain string in the other stops the
 * build and names itself in the error. It never degrades at runtime into a blank label.
 *
 * The runtime lookup still falls back (locale → English → the key itself) because a hot-fixed
 * catalogue in production must not blank a screen — but that path is unreachable while the
 * types hold.
 */

import en from './en.json'
import it from './it.json'
import type {
  AssertNever,
  KeyOf,
  Locale,
  Message,
  MissingFrom,
  PluralKeyOf,
  ShapeMismatch,
  SimpleKeyOf,
} from './shared'

type En = typeof en
type It = typeof it

/** Every key in the catalogue. Use it for props that carry a key. */
export type TKey = KeyOf<En>

/** Keys whose message has plural forms — `t()` demands a `count` param for these. */
export type PluralTKey = PluralKeyOf<En>

/** Keys whose message is one string. Prefer this for component props taking a key. */
export type SimpleTKey = SimpleKeyOf<En>

/* ── build-time parity ─────────────────────────────────────────────────────
   Exported so `noUnusedLocals` keeps them, and so a test can name them. */

/** Compile error naming any key `en.json` has and `it.json` lacks. */
export type ItalianCoversEnglish = AssertNever<MissingFrom<En, It>>
/** Compile error naming any key `it.json` has and `en.json` lacks. */
export type EnglishCoversItalian = AssertNever<MissingFrom<It, En>>
/** Compile error naming any key that is plural in one catalogue and singular in the other. */
export type PluralShapesAgree = AssertNever<ShapeMismatch<En, It>>

/* ── lookup ───────────────────────────────────────────────────────────────── */

export type Catalog = Readonly<Record<TKey, Message>>

export const CATALOGS: Readonly<Record<Locale, Catalog>> = { en, it }

/** locale → English → the key itself. Only the first branch runs while the types hold. */
export const messageFor = (locale: Locale, key: TKey): Message =>
  CATALOGS[locale][key] ?? CATALOGS.en[key] ?? key

/** Every key in the catalogue, sorted. Used by the language preview and by tests. */
export const ALL_KEYS: readonly TKey[] = Object.keys(en).sort() as TKey[]
